/**
 * Telehealth tile pricing — the Builder-authored price model + its resolver.
 *
 * Price is the ONE card field Builder owns. Everything else a telehealth tile
 * shows (name, image, brand, badges, ratings) comes from SFCC, because a second
 * place to manage the same data is a cost we only pay with a reason. The reason
 * here: the launch pricing includes a first-month discount, and SFCC can't
 * discount a product that isn't in the cart yet — so the displayed price has to
 * come from somewhere else. Nothing else on the card has that problem.
 *
 * Authors pick a `priceMode` and fill the one or two fields it reveals. Three of
 * the four modes stay structured (numbers in, `useFormatPrice` out) so currency
 * formatting, the was/now strikethrough and its screen-reader label are
 * identical to every other tile on the site. `custom` is the deliberate,
 * *visible* escape hatch for copy the structured modes can't express.
 *
 * Rendering is entirely the shared <ProductPrice>: this module only builds the
 * `PriceDisplay` it takes, so a pricing fix there still lands everywhere at once.
 *
 * Pure on purpose (no React, no contexts) — `formatPrice` is injected so the
 * resolver stays a leaf import that unit tests can drive directly.
 */
import { type PriceDisplay, wasNowDisplay } from "~/shop/utils/price-display";

/**
 * How a telehealth SKU's price is displayed. The mode is authored, not inferred,
 * so which SKUs have left the structured path is visible at a glance in Builder
 * rather than implied by which fields happen to be filled.
 */
export type TelehealthPriceMode =
  /** One price, no discount — e.g. `$1,249`. */
  | "single"
  /** Regular price struck through, discounted price shown — e.g. ~~$499~~ $475. */
  | "discounted"
  /** Master with frequency variants — e.g. `Starts at $229`. */
  | "startsAt"
  /** Verbatim copy the three structured modes can't express. */
  | "custom";

/** One authored row — the Builder fields for a single telehealth SKU. */
export interface TelehealthSkuPricing {
  /** SFCC product id, e.g. `80008t`. Matches `HecProduct.id`. */
  sku: string;
  priceMode: TelehealthPriceMode;
  /**
   * The real/regular price. Used by `single`, `discounted` (as the struck "was")
   * and `startsAt` (as the "from" figure). NOT the discounted number — that is
   * always {@link discountedPrice}, so an author never has to think about which
   * field the strikethrough lands on.
   */
  price?: number;
  /** The discounted price. `discounted` mode only. */
  discountedPrice?: number;
  /** Verbatim price copy. `custom` mode only. */
  priceText?: string;
}

/**
 * Authored pricing for the site, keyed by SKU. A Record (not the authored array)
 * so a tile's lookup is O(1) — a PLP can render every telehealth SKU at once.
 */
export type TelehealthTilePricing = Record<string, TelehealthSkuPricing>;

/**
 * No authored pricing. Deliberately empty rather than a hardcoded price list:
 * an unreachable Builder must never resurrect a stale medication price, and the
 * SFCC catalog price is the undiscounted number we moved away from — so a tile
 * with no entry shows no price at all and keeps its CTA.
 *
 * Frozen because it is module-scoped and handed back BY REFERENCE (see
 * {@link normalizeTelehealthPricing} and `getTelehealthTilePricing`). Module
 * scope outlives a request — and spans sites — in a Worker isolate, so a future
 * consumer that wrote into the returned record would poison every later request
 * on that isolate. Nothing does today; freezing makes it a throw rather than a
 * cross-site data bug.
 */
export const TELEHEALTH_PRICING_FALLBACK: TelehealthTilePricing = Object.freeze(
  {},
);

/** Prefix for `startsAt`. One string here, so eight cards can't drift apart. */
export const STARTS_AT_PREFIX = "Starts at";

const isPositive = (n: number | null | undefined): n is number =>
  typeof n === "number" && Number.isFinite(n) && n > 0;

/**
 * One entry's `data` exactly as Builder returns it — every field optional and
 * untyped, because the authored shape is not ours to guarantee.
 */
export interface BuilderPricingData {
  sku?: unknown;
  priceMode?: unknown;
  price?: unknown;
  discountedPrice?: unknown;
  priceText?: unknown;
}

/** The slice of a Builder entry this module reads. */
export interface BuilderPricingEntry {
  data?: BuilderPricingData | null;
}

const PRICE_MODES: readonly TelehealthPriceMode[] = [
  "single",
  "discounted",
  "startsAt",
  "custom",
];

/** Builder's wrapper for a field with localization switched on. */
const LOCALIZED_VALUE_TYPE = "@builder.io/core:LocalizedValue";

/**
 * Unwrap a Builder LocalizedValue to its underlying value.
 *
 * A localized field arrives as `{ "@type": "…LocalizedValue", Default: <value>,
 * [locale]: <value> }` instead of the bare value — observed on `priceMode` in
 * the first authored entry, where `sku` and `price` came through plain. Whether
 * a field is wrapped is an author-side toggle we don't control, so every field
 * is unwrapped defensively rather than just the one we caught.
 *
 * All three storefronts are en-US, so `Default` is the value; the first
 * non-`@type` key is a fallback for an entry authored with a named locale and
 * no default.
 */
const unwrapLocalized = (v: unknown): unknown => {
  if (typeof v !== "object" || v === null) return v;
  const obj = v as Record<string, unknown>;
  if (obj["@type"] !== LOCALIZED_VALUE_TYPE) return v;
  if ("Default" in obj) return obj.Default;
  const [first] = Object.keys(obj).filter(k => k !== "@type");
  return first === undefined ? undefined : obj[first];
};

const asMode = (raw: unknown): TelehealthPriceMode | null => {
  const v = unwrapLocalized(raw);
  return typeof v === "string" && (PRICE_MODES as readonly string[]).includes(v)
    ? (v as TelehealthPriceMode)
    : null;
};

/**
 * Builder number inputs arrive as numbers, but an entry authored before the
 * field existed (or hand-edited via the content API) can carry a string. Accept
 * a numeric string, reject anything else — a bad value becomes `undefined` and
 * the resolver degrades to no price rather than rendering `NaN`.
 */
const asPrice = (raw: unknown): number | undefined => {
  const v = unwrapLocalized(raw);
  if (typeof v === "number") return Number.isFinite(v) ? v : undefined;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
};

const asText = (raw: unknown): string | undefined => {
  const v = unwrapLocalized(raw);
  return typeof v === "string" && v.trim() !== "" ? v.trim() : undefined;
};

/**
 * Normalize the fetched entries into a SKU-keyed Record, dropping any entry
 * with no usable SKU or an unrecognized mode.
 *
 * One entry per SKU (not one entry holding a list) so each medication's price
 * gets Builder's own scheduling, publishing and rollback — a first-month
 * discount that starts on a date is authored per medication, which a single
 * shared entry could not express. Mirrors how `category-enrichment` keys
 * per-category content.
 *
 * The FIRST entry wins on a duplicate SKU, matching Builder's own priority
 * ordering in the response — a duplicate is an authoring error either way, but
 * this way the winner is the one Builder itself ranks first.
 *
 * Lives here rather than in the server module so it stays a pure leaf that unit
 * tests can drive without stubbing the Workers runtime.
 */
export function normalizeTelehealthPricing(
  // `unknown[]` rather than the SDK's `BuilderContent[]`: this function exists
  // to validate untrusted authored data, so claiming a shape on the way in
  // would be theatre. Every field is narrowed below.
  entries: readonly unknown[] | null | undefined,
): TelehealthTilePricing {
  if (!Array.isArray(entries)) return TELEHEALTH_PRICING_FALLBACK;

  // Null-prototype: SKUs are authored by hand, and a row keyed `constructor` or
  // `toString` would otherwise collide with `Object.prototype` — making the
  // `hasOwn` check below truthy on the FIRST occurrence and dropping a
  // legitimate row, or (for `__proto__`) setting the prototype instead of
  // adding a key. Unlikely, not impossible: this file already trims whitespace
  // because a real entry was authored as `"80008t "`.
  const out: TelehealthTilePricing = Object.create(null);
  for (const raw of entries) {
    const data = (raw as BuilderPricingEntry | null | undefined)?.data;
    if (!data) continue;

    const sku = asText(data.sku);
    const priceMode = asMode(data.priceMode);
    if (!sku || !priceMode) continue;
    if (Object.hasOwn(out, sku)) continue;

    out[sku] = {
      sku,
      priceMode,
      price: asPrice(data.price),
      discountedPrice: asPrice(data.discountedPrice),
      priceText: asText(data.priceText),
    };
  }
  return out;
}

/**
 * Build the `PriceDisplay` for one telehealth SKU, or null to render no price.
 *
 * Degradation is uniform: anything unauthored, unparseable or non-positive
 * yields null and the tile simply omits its price line. A telehealth card is
 * never worth showing a wrong number for.
 *
 * `startsAt` and `custom` both land on `setPriceText`, which <ProductPrice>
 * renders verbatim. That field is named for the product-set override
 * (`c_plpSetPrice`) it was introduced for; it is a display-model field rather
 * than a catalog one, so populating it from Builder is intended reuse of the
 * verbatim branch, not a collision.
 */
export function resolveTelehealthPriceDisplay(
  entry: TelehealthSkuPricing | undefined,
  // Matches `useFormatPrice()`, which can return undefined. Only `startsAt`
  // pre-formats (it has to — `setPriceText` renders verbatim); an undefined
  // result there degrades to no price rather than "Starts at undefined".
  formatPrice: (value: number) => string | undefined,
): PriceDisplay | null {
  if (!entry) return null;

  switch (entry.priceMode) {
    case "custom": {
      const text = entry.priceText?.trim();
      // Verbatim text carries no markup, so `custom` structurally cannot render
      // a strikethrough — a faked "$499 $475" would read as two plain numbers.
      // That is the point: every real was/now is funnelled into `discounted`,
      // where the strike and the "Was …, now …" label are handled for us.
      return text
        ? { price: 0, isOnSale: false, isRange: false, setPriceText: text }
        : null;
    }

    case "startsAt": {
      if (!isPositive(entry.price)) return null;
      const formatted = formatPrice(entry.price);
      return formatted
        ? {
            price: entry.price,
            isOnSale: false,
            isRange: false,
            setPriceText: `${STARTS_AT_PREFIX} ${formatted}`,
          }
        : null;
    }

    case "discounted":
    case "single":
      // Both go through the shared was/now resolver: `single` simply has no
      // discount to pass. This inherits its magnitude-sort (the struck price is
      // always the larger of the two, so an inverted entry still renders
      // sanely) and its "a non-positive sale is a missing value, not a free
      // product" rule — which is also what silently degrades a `discounted`
      // row with an empty discount field down to a plain price.
      return isPositive(entry.price)
        ? wasNowDisplay(entry.price, entry.discountedPrice)
        : null;

    default:
      return null;
  }
}
